import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { ShopHeader } from "@/components/shop/ShopHeader";
import { Footer } from "@/components/shared/Footer";
import { Button } from "@/components/ui/button";
import { Leaf, ArrowLeft } from "lucide-react";
import { getProductByHandle } from "@/lib/products";
import { useCartStore } from "@/stores/cartStore";
import { trackViewContent, trackAddToCart } from "@/lib/metaPixel";
import { trackViewContentEvent, trackAddToCartEvent } from "@/lib/analyticsTracker";
import { trackTiktokViewContent, trackTiktokAddToCart } from "@/lib/tiktokPixel";
import { sendTiktokServerEvent } from "@/lib/tiktokServerEvents";

import { AnnouncementBar } from "@/components/product/AnnouncementBar";
import { ProductHero } from "@/components/product/ProductHero";
import { ProblemSection } from "@/components/product/ProblemSection";
import { BenefitsGrid } from "@/components/product/BenefitsGrid";
import { TrustSection } from "@/components/product/TrustSection";
import { StickyCTA } from "@/components/product/StickyCTA";

const formatPrice = (amount: number) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount);

const ProductDetail = () => {
  const { handle } = useParams<{ handle: string }>();
  const navigate = useNavigate();
  const addItem = useCartStore((state) => state.addItem);

  const product = handle ? getProductByHandle(handle) : undefined;

  // Meta Pixel + TikTok Pixel + Analytics: ViewContent
  useEffect(() => {
    if (product) {
      trackViewContent({
        content_name: product.title,
        content_ids: [product.id],
        content_type: "product",
        value: product.price,
        currency: "EUR",
      });
      trackViewContentEvent(product.id, product.title, product.price);

      // TikTok: ViewContent (browser + server)
      trackTiktokViewContent({
        content_id: product.id,
        content_type: "product",
        content_name: product.title,
        value: product.price,
        currency: "EUR",
      });
      sendTiktokServerEvent({
        event: "ViewContent",
        event_id: `vc_${product.id}_${Date.now()}`,
        properties: {
          contents: [{ content_id: product.id, content_type: "product", content_name: product.title, price: product.price }],
          value: product.price,
          currency: "EUR",
        },
      });
    }
  }, [product?.id]);

  const handleBuyNow = () => {
    if (!product || !product.available) return;
    addItem({ product, quantity: 1 });
    trackAddToCart({
      content_name: product.title,
      content_ids: [product.id],
      content_type: "product",
      value: product.price,
      currency: "EUR",
      num_items: 1,
    });
    trackAddToCartEvent(product.id, product.title, 1, product.price);

    // TikTok: AddToCart (browser + server)
    trackTiktokAddToCart({
      content_id: product.id,
      content_type: "product",
      content_name: product.title,
      quantity: 1,
      value: product.price,
      currency: "EUR",
    });
    sendTiktokServerEvent({
      event: "AddToCart",
      event_id: `atc_${product.id}_${Date.now()}`,
      properties: {
        contents: [{ content_id: product.id, content_type: "product", content_name: product.title, quantity: 1, price: product.price }],
        value: product.price,
        currency: "EUR",
      },
    });

    navigate("/checkout");
  };

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <ShopHeader />
        <div className="container mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <Leaf className="w-10 h-10 text-primary/50" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">Produit non trouvé</h2>
          <p className="text-muted-foreground mb-6">Le produit que vous recherchez n'existe pas.</p>
          <Button onClick={() => navigate("/shop")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour à la boutique
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AnnouncementBar />
      <ShopHeader />
      <ProductHero product={product} onBuyNow={handleBuyNow} formatPrice={formatPrice} />
      <ProblemSection />
      <BenefitsGrid productId={product.id} />
      <TrustSection productId={product.id} price={product.price} />
      <Footer />
      <StickyCTA product={product} onBuyNow={handleBuyNow} formatPrice={formatPrice} />
    </div>
  );
};

export default ProductDetail;
